import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useToast } from '@/shared/contexts/ToastContext';
import { submitForm } from '@/shared/api';
import type { FormType } from '@/shared/api';

const nameEmailFormSchema = z.object({
  name: z
    .string()
    .min(1, 'Name is required')
    .min(2, 'Name must be at least 2 characters'),
  email: z
    .string()
    .min(1, 'Email is required')
    .email('Please enter a valid email address')
});

export type NameEmailFormData = z.infer<typeof nameEmailFormSchema>;

export interface NameEmailFormProps {
  onSubmit?: (data: NameEmailFormData) => Promise<void> | void;
  submitText?: string;
  submittingText?: string;
  namePlaceholder?: string;
  emailPlaceholder?: string;
  variant?: 'default' | 'footer' | 'hero' | 'case-study' | 'instant-demo';
  className?: string;
  showNameField?: boolean;
  onSuccessMessage?: string | ((data: NameEmailFormData) => string);
  formType?: FormType;
}

export const NameEmailForm = ({
  onSubmit,
  submitText = 'Submit',
  submittingText = 'Submitting...',
  namePlaceholder = 'Enter Your Name*',
  emailPlaceholder = 'Enter Your Email*',
  variant = 'default',
  className = '',
  showNameField = true,
  onSuccessMessage,
  formType
}: NameEmailFormProps) => {
  const { notify } = useToast();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting }
  } = useForm<NameEmailFormData>({
    resolver: zodResolver(nameEmailFormSchema)
  });

  // Map variant to form type if not explicitly provided
  const getFormType = (): FormType => {
    if (formType) return formType;

    switch (variant) {
      case 'footer':
        return 'footer-demo';
      case 'case-study':
        return 'case-study';
      case 'instant-demo':
        return 'instant-demo';
      case 'hero':
        return 'ready-to-get-started';
      default:
        return 'ready-to-get-started';
    }
  };

  const handleFormSubmit = async (data: NameEmailFormData) => {
    try {
      // Submit to API to save to file
      if (formType || variant !== 'default') {
        try {
          await submitForm({
            name: data.name,
            email: data.email,
            formType: getFormType()
          });
        } catch (apiError) {
          // Log error but don't block the user experience
          console.error('Failed to save form submission:', apiError);
        }
      }

      // Call custom onSubmit if provided
      if (onSubmit) {
        await onSubmit(data);
      } else {
        // Default behavior: simulate API call
        await new Promise(resolve => setTimeout(resolve, 1000));
      }

      const message =
        typeof onSuccessMessage === 'function'
          ? onSuccessMessage(data)
          : onSuccessMessage ||
            `Thank you ${data.name}! We'll be in touch soon.`;
      notify('success', message);
      reset();
    } catch (error) {
      console.error('Form submission error:', error);
      notify('error', 'Something went wrong. Please try again.');
    }
  };

  // Variant-specific class names
  const getVariantClasses = () => {
    switch (variant) {
      case 'footer':
        return {
          container: 'demo-form',
          inputGroup:
            'input-group r-06 rounded-[6px] relative flex flex-wrap items-stretch w-full bg-[#f4f4f4] border px-[2px] py-[9px] border-solid border-transparent mb-[10px]',
          input:
            'form-control block xl:!h-[32px] bg-transparent !text-[#666] xl:!text-[1rem] !font-normal shadow-none p-[0_10px] border-[none] focus:shadow-none focus:border-[none] lg:max-xl:!h-[28px] md:max-lg:!h-[28px] sm:max-md:!text-[1.0625rem] sm:max-md:!h-[32px] xsm:max-sm:!text-[1.1rem] xsm:max-sm:!h-[34px] placeholder:text-[#6c757d] focus:[outline:0] relative flex-auto !w-[1%] min-w-0 !m-0 !leading-[1.5] rounded-[6px]',
          button:
            'btn btn--theme hover--theme submit w-full xl:!h-[58px] p-[16px] lg:max-xl:!h-[54px] lg:max-xl:!p-[16px] md:max-lg:!h-[48px] md:max-lg:!text-[1.0625rem] md:max-lg:!p-[16px] sm:max-md:!h-[56px] sm:max-md:!text-[1.0625rem] sm:max-md:!p-[16px]',
          error: 'form-notification text-accent-error block mb-[10px]'
        };
      case 'hero':
        return {
          container: 'flex flex-wrap mx-[calc(-0.5*_1.5rem)] request-form',
          inputGroup:
            'w-full flex-[0_0_auto] px-[calc(0.5*_1.5rem)] max-w-full',
          input:
            'form-control block w-full name xl:!h-[58px] bg-[#f5f6f8] [border:1px_solid_transparent] !text-[#666] xl:!text-[1rem] !font-normal transition-all duration-[400ms] ease-[ease-in-out] !mb-[22px] lg:max-xl:!h-[54px] md:max-lg:!h-[48px] md:max-lg:!text-[1.0625rem] md:max-lg:px-[14px] md:max-lg:py-0 sm:max-md:!h-[56px] sm:max-md:!text-[1.0625rem] sm:max-md:px-[14px] sm:max-md:py-0 xsm:max-sm:!h-[56px] xsm:max-sm:!text-[1.0625rem] xsm:max-sm:px-[14px] xsm:max-sm:py-0 focus:bg-white focus:shadow-none focus:border-[#1680fb] focus:[outline:0] leading-normal bg-clip-padding appearance-none p-[.375rem_.75rem] rounded-[0.375rem] placeholder:text-[#666]',
          button:
            'btn btn--theme hover--theme submit w-full !h-[58px] p-[16px]',
          error: 'text-sm text-accent-error mt-1'
        };
      case 'case-study':
        return {
          container:
            'flex flex-col md:flex-row gap-4 max-w-5xl mx-auto items-stretch md:items-start justify-center',
          inputGroup: 'w-full md:w-[240px]',
          input:
            'form-control block w-full name xl:!h-[58px] bg-[#f5f6f8] [border:1px_solid_transparent] !text-[#666] xl:!text-[1rem] !font-normal transition-all duration-[400ms] ease-[ease-in-out] lg:max-xl:!h-[54px] md:max-lg:!h-[48px] md:max-lg:!text-[1.0625rem] md:max-lg:px-[14px] md:max-lg:py-0 sm:max-md:!h-[56px] sm:max-md:!text-[1.0625rem] sm:max-md:px-[14px] sm:max-md:py-0 xsm:max-sm:!h-[56px] xsm:max-sm:!text-[1.0625rem] xsm:max-sm:px-[14px] xsm:max-sm:py-0 focus:bg-white focus:shadow-none focus:border-[#1680fb] focus:[outline:0] leading-normal bg-clip-padding appearance-none p-[.375rem_.75rem] rounded-[0.375rem] placeholder:text-[#666] font-Jakarta',
          button:
            'bg-[#007BFF] hover:bg-[#0056b3] disabled:bg-[#007BFF]/50 disabled:cursor-not-allowed text-white font-Jakarta font-semibold px-8 rounded-[0.375rem] transition-colors duration-200 xl:!h-[58px] lg:max-xl:!h-[54px] md:max-lg:!h-[48px] sm:max-md:!h-[56px] xsm:max-sm:!h-[56px] text-base lg:max-xl:text-sm md:max-lg:text-sm sm:max-md:text-sm xsm:max-sm:text-sm whitespace-nowrap w-full md:w-auto',
          error: 'text-sm text-accent-error font-Jakarta'
        };
      case 'instant-demo':
        return {
          container: 'request-form',
          inputGroup: 'w-full mb-[22px]',
          input:
            'form-control name block w-full leading-normal bg-clip-padding appearance-none p-[.375rem_.75rem] rounded-[0.375rem] xl:!h-[58px] bg-[#f5f6f8] [border:1px_solid_transparent] !text-[#666] xl:!text-[1rem] !font-normal transition-all duration-[400ms] ease-[ease-in-out] lg:max-xl:!h-[54px] md:max-lg:!h-[48px] md:max-lg:!text-[1.0625rem] md:max-lg:px-[14px] md:max-lg:py-0 sm:max-md:!h-[56px] sm:max-md:!text-[1.0625rem] sm:max-md:px-[14px] sm:max-md:py-0 xsm:max-sm:!h-[56px] xsm:max-sm:!text-[1.0625rem] xsm:max-sm:px-[14px] xsm:max-sm:py-0 focus:bg-white focus:shadow-none focus:border-[#1680fb] focus:[outline:0] placeholder:text-[#666]',
          button:
            'btn btn--theme hover--theme submit w-full xl:!h-[58px] p-[16px] lg:max-xl:!h-[54px] lg:max-xl:!p-[16px] md:max-lg:!h-[48px] md:max-lg:!text-[1.0625rem] md:max-lg:!p-[16px] sm:max-md:!h-[56px] sm:max-md:!text-[1.0625rem] sm:max-md:!p-[16px]',
          error: 'text-sm text-accent-error mt-1'
        };
      default:
        return {
          container: '',
          inputGroup: '',
          input: '',
          button: '',
          error: ''
        };
    }
  };

  const variantClasses = getVariantClasses();
  const inputErrorClass =
    variant === 'footer'
      ? ''
      : errors.name || errors.email
        ? '!border-accent-error'
        : '';

  return (
    <form
      className={`${variantClasses.container} ${className}`}
      onSubmit={handleSubmit(handleFormSubmit)}
    >
      {showNameField && (
        <div className={variantClasses.inputGroup || 'w-full mb-4'}>
          {variant === 'footer' ? (
            <>
              <div className={variantClasses.inputGroup}>
                <input
                  type="text"
                  {...register('name')}
                  className={`${variantClasses.input} ${inputErrorClass}`}
                  placeholder={namePlaceholder}
                  autoComplete="off"
                  required
                  id="form-name"
                />
              </div>
              {errors.name && (
                <label htmlFor="form-name" className={variantClasses.error}>
                  {errors.name.message}
                </label>
              )}
            </>
          ) : (
            <>
              <input
                type="text"
                {...register('name')}
                className={`${variantClasses.input} ${inputErrorClass}`}
                placeholder={namePlaceholder}
                autoComplete="off"
                disabled={isSubmitting}
              />
              {errors.name && (
                <p
                  className={
                    variantClasses.error || 'text-sm text-accent-error mt-1'
                  }
                >
                  {errors.name.message}
                </p>
              )}
            </>
          )}
        </div>
      )}

      <div className={variantClasses.inputGroup || 'w-full mb-4'}>
        {variant === 'footer' ? (
          <>
            <div className={variantClasses.inputGroup}>
              <input
                type="email"
                {...register('email')}
                className={`${variantClasses.input} ${inputErrorClass}`}
                placeholder={emailPlaceholder}
                autoComplete="off"
                required
                id="form-email"
              />
            </div>
            {errors.email && (
              <label htmlFor="form-email" className={variantClasses.error}>
                {errors.email.message}
              </label>
            )}
          </>
        ) : (
          <>
            <input
              type="email"
              {...register('email')}
              className={`${variantClasses.input} ${inputErrorClass}`}
              placeholder={emailPlaceholder}
              autoComplete="off"
              disabled={isSubmitting}
            />
            {errors.email && (
              <p
                className={
                  variantClasses.error || 'text-sm text-accent-error mt-1'
                }
              >
                {errors.email.message}
              </p>
            )}
          </>
        )}
      </div>

      <div
        className={
          variant === 'case-study'
            ? 'w-full md:w-auto'
            : variant === 'footer'
              ? 'w-full form-btn'
              : 'w-full form-btn'
        }
      >
        <button
          type="submit"
          disabled={isSubmitting}
          className={`${variantClasses.button} ${isSubmitting ? 'disabled:cursor-not-allowed' : ''}`}
        >
          {isSubmitting ? submittingText : submitText}
        </button>
      </div>

      {/* Error messages container for case-study variant */}
      {variant === 'case-study' && (errors.name || errors.email) && (
        <div className="max-w-5xl mx-auto mt-2 text-center">
          {errors.name && (
            <p className={variantClasses.error}>{errors.name.message}</p>
          )}
          {errors.email && (
            <p className={variantClasses.error}>{errors.email.message}</p>
          )}
        </div>
      )}
    </form>
  );
};
